import { useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import _ from 'lodash';
import useStores from 'shared/hooks/use-stores';
import useDispensaryPaymentMethods from 'src/hooks/use-dispensary-payment-methods';
import { useDutchiePay } from 'src/payments/hooks';
import { CANADA_CREDIT_CART_PAYMENT_METHODS, DUTCHIE_PAY } from 'src/payments/constants';
import { useCheckoutLineItems } from 'src/checkout/hooks/use-checkout-line-items';
import { EnrollmentBankAccount } from 'src/state/dutchiePay-enrollment';

import {
  categorizePaymentOptions,
  DispensaryPaymentOptions,
  getEnabledPayByBankOrderTypesNotSelected,
  getMaxPaymentFee,
  getOnlinePaymentFee,
  normalizePaymentFeeKeys,
} from './utils';

export type UseCartPaymentResponse = Omit<ReturnType<typeof useDutchiePay>, 'paymentMethodId'> & {
  paymentMethod: string;
  bankInformation: EnrollmentBankAccount | null;
  otherOrderTypesWithPBBEnabled: string[];
  hasOtherOrderTypesWithPBBEnabled: boolean;
  payNowTabDisabled: boolean;
  onlinePaymentOption: DispensaryPaymentOptions[number] | null;
  onlinePaymentOptionValue: string;
  hasOnlineAndOfflinePaymentOptions: boolean;
  paymentMethodId: number | string | null;
  showBankInformation: boolean;
  showCreditCardInformation: boolean;
  onlinePaymentOptionIsDutchiePay: boolean;
  paymentMethodIsDutchiePay: boolean;
  pbbSignupEnabledForOrderType: boolean;
  onlinePaymentOptionFeeLabel: string;
  onlinePaymentOptionIsCreditCard: boolean;
  maxInstoreFeeLabel: string;
  defaultOfflinePaymentValue: string;
  offlinePaymentOptionValues: string[];
  loggedInPBBSignupEnabled: boolean;
  loggedOutPBBSignupEnabled: boolean;
  payByBankSelected: boolean;
};

export const useCartPayment = (): UseCartPaymentResponse => {
  // Hooks
  const { Cart, User } = useStores();
  const flags = useFlags();

  const lineItemData = useCheckoutLineItems();
  const paymentOptions = useDispensaryPaymentMethods();
  const dutchiePayDetails = useDutchiePay();
  // Cart
  const { order, orderType, orderOptions } = Cart ?? {};
  const { dispensary, paymentMethod } = order ?? {};
  const { paymentFees = [] } = dispensary ?? {};
  const normalizedPaymentFees = normalizePaymentFeeKeys(paymentFees);
  const {
    bankAccounts,
    isEnrolledDutchiePay,
    allowDutchiePayEnrollment,
    isDutchiePayEnabledForDispo,
    disablePBBSignupForCuraleafPlus,
  } = dutchiePayDetails;

  const { isLoggedIn } = User ?? {};
  const disableTabFees = flags['fintech.platform.remove-tab-fees.rollout'];
  const hidePBBWhenDisabledForOrderType =
    flags['ecomm.checkout.hide-pbb-when-disabled-for-order-type.rollout'] ?? false;
  const plaidEmbeddeedSearchEnabled = flags['fintech.platform.dutchiepay.plaid-embedded-search'] ?? false;
  const isAeroSyncEnabled = flags[`fintech.platform.enable-aerosync-enrollment.rollout`] ?? false;

  // Memoized values
  const orderTotal = useMemo(() => _.find(lineItemData, ['type', 'total'])?.value ?? 0, [lineItemData]);
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const bankInformation = useMemo(() => (bankAccounts?.length > 0 ? bankAccounts[0] : null), [bankAccounts]);
  const { offlinePaymentOptions, onlinePaymentOption: availableOnlinePaymentOption } = useMemo(
    () => categorizePaymentOptions(paymentOptions ?? []),
    [paymentOptions]
  );

  const onlinePaymentOption =
    availableOnlinePaymentOption?.value === DUTCHIE_PAY && !isDutchiePayEnabledForDispo
      ? null
      : availableOnlinePaymentOption;

  //  This is a list of order types that have PBB enabled but are not the selected order type
  const otherOrderTypesWithPBBEnabled = useMemo(
    () => getEnabledPayByBankOrderTypesNotSelected({ orderOptions, dispensary, selectedOrderType: orderType }),
    [orderOptions, dispensary, orderType]
  );
  // Derived Payment Options Values
  const onlinePaymentOptionValue = onlinePaymentOption?.value ?? '';
  const offlinePaymentOptionValues = offlinePaymentOptions.map(({ value }) => value);
  const defaultOfflinePaymentValue = offlinePaymentOptionValues.length > 0 ? offlinePaymentOptionValues[0] : '';
  const onlinePaymentOptionIsDutchiePay = onlinePaymentOptionValue === DUTCHIE_PAY;
  const onlinePaymentOptionIsCreditCard = CANADA_CREDIT_CART_PAYMENT_METHODS.includes(onlinePaymentOptionValue);
  const hasOtherOrderTypesWithPBBEnabled = !disablePBBSignupForCuraleafPlus && !!otherOrderTypesWithPBBEnabled.length;
  // Flagged: no online option for this order type, so 'Pay now' only cross-sells PBB for other order types.
  // Without offline options, keep the online-only cross-sell view instead.
  const payNowTabDisabled =
    hidePBBWhenDisabledForOrderType &&
    !!offlinePaymentOptions.length &&
    !onlinePaymentOption &&
    hasOtherOrderTypesWithPBBEnabled;
  // Derived Payment Method Values
  const paymentMethodId = bankInformation?.id ?? null;
  const paymentMethodIsDutchiePay = paymentMethod === DUTCHIE_PAY;
  const paymentMethodIsCreditCard = CANADA_CREDIT_CART_PAYMENT_METHODS.includes(paymentMethod);
  const hasOnlineAndOfflinePaymentOptions =
    !!offlinePaymentOptions.length && (!!onlinePaymentOption || hasOtherOrderTypesWithPBBEnabled);
  // Fee Labels
  const maxInstoreFeeLabel = disableTabFees
    ? ''
    : getMaxPaymentFee({
        paymentTypes: offlinePaymentOptionValues,
        paymentFees: normalizedPaymentFees,
        orderTotal,
      });

  const onlinePaymentOptionFeeLabel = getOnlinePaymentFee(
    onlinePaymentOptionValue,
    normalizedPaymentFees,
    disableTabFees
  );
  // Conditional Rendering
  const showBankInformation =
    isEnrolledDutchiePay &&
    !!isLoggedIn &&
    !!bankInformation &&
    onlinePaymentOptionIsDutchiePay &&
    paymentMethodIsDutchiePay;

  const showCreditCardInformation = onlinePaymentOptionIsCreditCard && paymentMethodIsCreditCard;

  const pbbSignupEnabledForOrderType =
    allowDutchiePayEnrollment && paymentMethodIsDutchiePay && onlinePaymentOptionIsDutchiePay;

  const loggedInPBBSignupEnabled =
    plaidEmbeddeedSearchEnabled && isLoggedIn && pbbSignupEnabledForOrderType && !isAeroSyncEnabled;

  const loggedOutPBBSignupEnabled = !loggedInPBBSignupEnabled && pbbSignupEnabledForOrderType;

  const payByBankSelected = onlinePaymentOptionIsDutchiePay && paymentMethodIsDutchiePay;

  return {
    ...dutchiePayDetails,
    paymentMethod,
    paymentMethodId,
    otherOrderTypesWithPBBEnabled,
    hasOtherOrderTypesWithPBBEnabled,
    payNowTabDisabled,
    onlinePaymentOption,
    onlinePaymentOptionValue,
    hasOnlineAndOfflinePaymentOptions,
    pbbSignupEnabledForOrderType,
    bankInformation,
    onlinePaymentOptionIsDutchiePay,
    paymentMethodIsDutchiePay,
    showBankInformation,
    showCreditCardInformation,
    onlinePaymentOptionFeeLabel,
    onlinePaymentOptionIsCreditCard,
    maxInstoreFeeLabel,
    defaultOfflinePaymentValue,
    offlinePaymentOptionValues,
    loggedInPBBSignupEnabled,
    loggedOutPBBSignupEnabled,
    payByBankSelected,
  };
};
